/* ── Drone TSL Shader Module ───────────────────────────────────
 * TSL (Three.js Shading Language) builders for the drone renderer's
 * NodeMaterial pipeline.
 *
 * Exposed builders:
 *   - `uSunDirection`, `uRimStrength` — module-scoped uniforms
 *     shared with DroneRenderer's game loop.
 *   - `time` (TSL built-in) — auto-updated seconds timer, used in all Fn bodies.
 *   - `buildDroneVertexDisplacement` — vertex position displacement for
 *     the spawn-scan materialization sweep.
 *   - `buildDroneEmissive` — additive emissive node composing engine core
 *     pulse, invulnerable hex armor, spawn scan glow, rim lighting, and
 *     sun glint. Writes non-zero emissive so the MRT bloom target picks
 *     up the drone body.
 *   - `buildDroneColor` — ghost-grid overlay for the not-yet-materialized
 *     region of a respawning drone.
 *   - `buildDroneOpacity` — spawn-progress alpha ramp.
 *   - `buildTrailColor`, `buildVaporTrailColor`, `buildCompressionRingColor`,
 *     `buildShadowColor`, `buildDataLinkColor`, `buildEcmColor`,
 *     `buildEcmSize` — fragment/vertex expressions for the 6 aux materials.
 * ───────────────────────────────────────────────────────────── */

import * as THREE from 'three/webgpu';
import {
  Fn,
  If,
  vec2,
  vec3,
  vec4,
  float,
  uniform,
  time,
  mix,
  abs,
  fract,
  floor,
  mod,
  max,
  sin,
  dot,
  length,
  normalize,
  smoothstep,
  step,
  pow,
  saturate,
  attribute,
  positionLocal,
  positionView,
  positionWorld,
  normalLocal,
  normalWorld,
  cameraPosition,
  uv,
  vertexColor,
  varyingProperty,
  select,
  atan,
} from 'three/tsl';
import { TslNode, hash21 } from './tslUtils';

/* ── Module-scoped uniforms ────────────────────────────────── */

export const uSunDirection = uniform(new THREE.Vector3(0.3, 0.9, 0.2));
export const uRimStrength = uniform(0.8);

/* ── Per-instance attribute helpers ────────────────────────── */

const aSpawnProgress: TslNode = attribute('aSpawnProgress', 'float');
const aInvulnerable: TslNode = attribute('aInvulnerable', 'float');
const aIsLocal: TslNode = attribute('aIsLocal', 'float');

/** Instance color written via `InstancedMesh.setColorAt`. Varying name set by
 * `InstanceNode.setup()` — this gives us the per-drone tactical color inside
 * the fragment shader without relying on colorNode auto-composition. */
const vInstanceColor = varyingProperty('vec3', 'vInstanceColor');

/* ── Drone core geometry Y bounds ─────────────────────────── */
// The drone body geometries are scaled by 4 in mergeAndDispose, producing a
// local Y range of approximately [-2.4, 2.4].
const SCAN_Y_MIN = float(-2.4);
const SCAN_Y_MAX = float(2.4);

/**
 * Hex cell lookup — returns the offset to the nearest hex center given a
 * tiled 2D coordinate with period `(sqrt(3), 1)`.
 */
const hexCell = /*@__PURE__*/ Fn(([p]: [ReturnType<typeof vec2>]) => {
  const period = vec2(1.732, 1.0);
  const offset = vec2(0.866, 0.5);
  const a = mod(p, period).sub(offset);
  const b = mod(p.add(offset), period).sub(offset);
  return select(dot(a, a).lessThan(dot(b, b)), a, b);
});

/* ── Drone body shaders ────────────────────────────────────── */

/**
 * Vertex-stage displacement for the spawn materialization sweep. Outputs the
 * displaced local position that the material uses as `positionNode`.
 *
 * The sweep pushes vertices outward along the local normal by 0.12 units in a
 * narrow band below the scan Y line, giving a bulging "materialization" look.
 */
export const buildDroneVertexDisplacement = /*@__PURE__*/ Fn(() => {
  const displaced = positionLocal.toVar();
  const scanY = mix(SCAN_Y_MIN, SCAN_Y_MAX, aSpawnProgress);
  const belowScan = smoothstep(scanY.sub(0.3), scanY, positionLocal.y).oneMinus();
  const push = normalLocal.mul(belowScan).mul(0.12);

  If(aSpawnProgress.lessThan(1.0), () => {
    displaced.assign(displaced.add(push));
  });

  return displaced;
});

/**
 * Composes the drone body's emissive output:
 *   1. Engine core pulse (additive per-instance colored glow in the rear
 *      axial cone).
 *   2. Invulnerability hex armor grid (additive during respawn invuln window).
 *   3. Spawn scan line (bright sweep during materialize).
 *   4. Fresnel rim lighting (boost for local drones).
 *   5. Sun glint (warm specular spike along the sun direction).
 *   6. Ambient lift — folded in so the MRT bloom target picks up the whole drone.
 *
 * This Fn is how the drone body writes into the MRT `emissive` render target.
 * As long as the return value is non-zero for any visible drone, selective
 * bloom will highlight the drone.
 */
export const buildDroneEmissive = /*@__PURE__*/ Fn(() => {
  const local = positionLocal;
  const axialDistance = abs(local.z);
  const radialDist = length(local.xy);

  // Engine core glow (frequency 7.0, offset by axial distance, amplitude 0.3,
  // bias 0.7, peak scale 2.45).
  const engineCore = smoothstep(1.8, 0.22, radialDist).mul(
    smoothstep(0.9, 3.8, axialDistance),
  );
  const enginePulse = float(0.7).add(
    sin(time.mul(7.0).add(axialDistance.mul(1.4))).mul(0.3),
  );
  const engineTerm = vInstanceColor.mul(engineCore).mul(enginePulse).mul(2.45);

  // Ambient lift: additive instance color + constant warmth folded into
  // emissive so the MRT bloom target picks up the drone body.
  const ambientLift = vInstanceColor.mul(0.24).add(vec3(0.08, 0.09, 0.1));

  // Rim lighting — Fresnel against the view direction, boosted for local drones.
  const viewDir = normalize(cameraPosition.sub(positionWorld));
  const rimFactor = saturate(dot(normalWorld, viewDir));
  const rim = pow(rimFactor.oneMinus(), 3.0);
  const localRimBoost = select(aIsLocal.greaterThan(0.5), float(1.8), float(1.0));
  const rimTerm = vInstanceColor.mul(rim).mul(uRimStrength).mul(localRimBoost);

  // Sun glint — warm spike along the sun direction.
  const sunGlintFactor = pow(
    max(dot(normalWorld, normalize(uSunDirection)), 0.0),
    6.0,
  );
  const sunGlintTerm = vec3(1.0, 0.94, 0.84).mul(sunGlintFactor).mul(0.09);

  const total = engineTerm.add(ambientLift).add(rimTerm).add(sunGlintTerm).toVar();

  // Invulnerability hex armor grid — pulses for a short window after respawn.
  If(aInvulnerable.greaterThan(0.01), () => {
    const hexUV = local.xz.mul(2.8).add(local.y.mul(0.6));
    const hc = hexCell(hexUV);
    const hexEdge = smoothstep(0.38, 0.42, length(hc));
    const armorPulse = float(0.5).add(
      sin(time.mul(3.5).add(local.y.mul(2.0))).mul(0.5),
    );
    const armorGlow = hexEdge.mul(armorPulse).mul(aInvulnerable).mul(0.22);
    total.addAssign(vec3(0.85, 0.88, 0.82).mul(armorGlow));
  });

  // Spawn scan glow — bright sweep line along the current materialization Y.
  If(aSpawnProgress.lessThan(1.0), () => {
    const scanY = mix(SCAN_Y_MIN, SCAN_Y_MAX, aSpawnProgress);
    const scanDist = abs(local.y.sub(scanY));
    const scanLine = smoothstep(0.25, 0.0, scanDist);
    total.addAssign(vec3(0.6, 0.75, 0.55).mul(scanLine).mul(0.5));
  });

  return total;
});

/**
 * Ghost-grid overlay for the area below the spawn scan line. Replaces the
 * base color only in the not-yet-materialized region. Takes a base color so
 * the vertex-color + instance-color multiplication chain stays intact.
 */
export const buildDroneColor = /*@__PURE__*/ Fn(([baseColor]: [ReturnType<typeof vec3>]) => {
  const result = baseColor.toVar();

  If(aSpawnProgress.lessThan(1.0), () => {
    const local = positionLocal;
    const scanY = mix(SCAN_Y_MIN, SCAN_Y_MAX, aSpawnProgress);
    const belowScan = smoothstep(scanY.sub(0.15), scanY, local.y).oneMinus();

    If(belowScan.greaterThan(0.01), () => {
      const gridUV = local.xz.mul(3.0);
      const gridX = smoothstep(0.04, 0.0, abs(fract(gridUV.x).sub(0.5)).sub(0.46));
      const gridZ = smoothstep(0.04, 0.0, abs(fract(gridUV.y).sub(0.5)).sub(0.46));
      const grid = max(gridX, gridZ);
      const ghostColor = vec3(0.35, 0.45, 0.3);
      const ghostMix = ghostColor.add(grid.mul(0.25));
      result.assign(mix(result, ghostMix, belowScan.mul(0.7)));
    });
  });

  return result;
});

/**
 * Spawn alpha ramp — fades the drone in as `aSpawnProgress` rises from 0
 * to 1. Multiplied onto the fragment opacity via `material.opacityNode`.
 */
export const buildDroneOpacity = /*@__PURE__*/ Fn(() => {
  const result = float(1).toVar();

  If(aSpawnProgress.lessThan(1.0), () => {
    const local = positionLocal;
    const scanY = mix(SCAN_Y_MIN, SCAN_Y_MAX, aSpawnProgress);
    const materialized = smoothstep(0.0, 0.15, aSpawnProgress).mul(
      mix(float(0.25), float(1.0), smoothstep(scanY.sub(0.3), scanY.add(0.1), local.y)),
    );
    result.assign(materialized);
  });

  return result;
});

/* ── Trail / vapor / compression ring / shadow / datalink / ecm ── */

/**
 * Drone trail material color/alpha builder. Outputs a `vec4` with
 * additive-blended color and a distance+scan-driven alpha, discarding low
 * alpha values via `alphaTestNode` elsewhere.
 */
export const buildTrailColor = /*@__PURE__*/ Fn(() => {
  const pos = uv().toVar();
  pos.y = pos.y.sub(time.mul(6.0));

  // Grid-like noise — two axis-shuffled hashes blended for scanline texture.
  const n1 = hash21(floor(pos.mul(vec2(20.0, 5.0))));
  const n2 = hash21(floor(pos.mul(vec2(10.0, 15.0))));
  const noise = mix(n1, n2, 0.5);
  const smoothNoise = smoothstep(0.4, 0.6, noise);

  const centerDist = abs(uv().x.sub(0.5)).mul(2.0);
  const core = smoothstep(0.5, 0.0, centerDist);
  const fade = smoothstep(1.0, 0.1, uv().y);
  const scan = step(0.5, fract(uv().y.mul(20.0).sub(time.mul(15.0))));

  const alpha = fade.mul(core.mul(0.7).add(smoothNoise.mul(0.5))).mul(
    float(0.8).add(scan.mul(0.4)),
  );

  const hotCore = mix(vInstanceColor, vec3(1.0, 0.9, 0.8), core.mul(0.5));
  return vec4(hotCore.mul(2.0), alpha);
});

/**
 * Vapor trail material color/alpha builder — soft white condensation with
 * radial falloff, modulated by per-instance color's red channel (used as an
 * intensity factor from DroneRenderer when setting `setColorAt`).
 */
export const buildVaporTrailColor = /*@__PURE__*/ Fn(() => {
  const centerDist = abs(uv().x.sub(0.5)).mul(2.0);
  const core = smoothstep(0.6, 0.0, centerDist);
  const fade = smoothstep(1.0, 0.1, uv().y);
  const base = vec3(0.85, 0.88, 0.9);
  const alpha = fade.mul(core).mul(float(0.5).mul(vInstanceColor.r));
  return vec4(base, alpha);
});

/**
 * Shadow disc material color/alpha builder — radial dark patch modulated
 * by a per-instance strength (red channel of `setColorAt`).
 */
export const buildShadowColor = /*@__PURE__*/ Fn(() => {
  const centered = uv().sub(0.5);
  const radial = smoothstep(0.16, 0.5, length(centered)).oneMinus();
  const alpha = radial.mul(radial).mul(0.26).mul(vInstanceColor.r);
  return vec4(vec3(0.02, 0.03, 0.05), alpha);
});

/**
 * Sonic compression ring material color/alpha builder — Fresnel edge
 * highlight on a torus, fading out as the ring expands.
 */
export const buildCompressionRingColor = /*@__PURE__*/ Fn(
  ([uProgress]: [TslNode]) => {
    const viewDir = normalize(cameraPosition.sub(positionWorld));
    const fresnel = pow(abs(dot(normalWorld, viewDir)).oneMinus(), 2.0);
    const base = vec3(0.9, 0.92, 0.95);
    const alpha = fresnel.mul(0.3).mul(uProgress.oneMinus());
    return vec4(base, alpha);
  },
);

/**
 * Data-link line color/alpha builder — `lineDistance` attribute + time-driven
 * pulse approximating data packets moving between local drones. `color`
 * vertex attribute carries a per-segment intensity written from JS.
 */
export const buildDataLinkColor = /*@__PURE__*/ Fn(() => {
  const lineDist: TslNode = attribute('lineDistance', 'float');
  const vColor = vertexColor().xyz;

  const packetFreq = float(0.12);
  const speed = float(1.8);
  const t = fract(lineDist.mul(packetFreq).sub(time.mul(speed)));
  const packet = smoothstep(0.35, 0.0, abs(t.sub(0.5)));
  const baseBrightness = float(0.18);
  const brightness = baseBrightness.add(packet.mul(0.82));

  const c2Color = vec3(0.2, 0.65, 0.7);
  return vec4(c2Color.mul(brightness).mul(vColor), brightness.mul(0.8));
});

/**
 * ECM particle fragment color/alpha — soft circle with a brightness falloff
 * and a per-vertex alpha attribute for flickering.
 */
export const buildEcmColor = /*@__PURE__*/ Fn(() => {
  const alphaAttr: TslNode = attribute('alpha', 'float');
  // pointUV (gl_PointCoord) is not valid WGSL; points are 1px in WebGPU so
  // a circle mask has no visual effect — return solid color directly.
  const ecmColor = vec3(0.7, 0.85, 0.9);
  return vec4(ecmColor, alphaAttr);
});

/**
 * ECM particle size builder — perspective-correct per-vertex sizing matching
 * the EnvironmentParticles pattern. Divides `ECM_SIZE_SCALE` by the abs
 * view-space Z depth so nearer particles appear larger, then multiplies by
 * the per-vertex `size` attribute (values 1.5–3.5 world units).
 *
 * `sizeAttenuation` must be `false` on the material when this node is active;
 * the Three.js built-in would double-apply perspective division.
 */
const ECM_SIZE_SCALE = 80.0;
export const buildEcmSize = /*@__PURE__*/ Fn(() => {
  const sizeAttr: TslNode = attribute('size', 'float');
  return positionView.z.abs().reciprocal().mul(ECM_SIZE_SCALE).mul(sizeAttr);
});

/**
 * Per-instance ability cooldown fraction in [0, 1]:
 *   0.0 → cooldown ready (full ring visible)
 *   1.0 → just used (ring nearly empty)
 * Written via `InstancedBufferAttribute` named `aCooldownFraction` on the
 * cooldown-arc InstancedMesh in DroneRenderer. Per-instance accent color is
 * uploaded separately via `setColorAt`.
 */
const aCooldownFraction: TslNode = attribute('aCooldownFraction', 'float');

/**
 * Cooldown arc shader — colors a ring around each drone's base proportional
 * to ability readiness. Reads:
 *   - `vInstanceColor` (RGB)  — per-drone-type accent color (`setColorAt`)
 *   - `aCooldownFraction`     — remaining cooldown fraction in [0, 1]
 *
 * The ring uses RingGeometry's UVs centered around (0.5, 0.5). Angle θ is
 * derived from `atan(uv.y - 0.5, uv.x - 0.5)` (range -π..π), normalized to
 * [0, 1] starting from the top (12 o'clock) going clockwise. A fragment is
 * lit when its angular position is within the "filled" portion of the arc:
 *
 *   filled = 1 - aCooldownFraction       // remaining of the arc
 *   visible if angleNorm < filled
 *
 * When `aCooldownFraction === 0`, `filled === 1` → entire ring lit.
 * When `aCooldownFraction === 1`, `filled === 0` → ring invisible.
 */
export const buildCooldownArcColor = /*@__PURE__*/ Fn(() => {
  // Re-center UV around (0.5, 0.5) so atan returns the polar angle.
  const centered = uv().sub(0.5);
  // atan(y, x) — TSL emits atan2 under WebGPU. Range: (-π, π].
  const theta = atan(centered.y, centered.x);
  // Normalize to [0, 1) starting from the top (12 o'clock), going clockwise.
  // Equivalent to: ((π/2 - theta) / (2π)) mod 1
  const TWO_PI = float(6.283185307179586);
  const HALF_PI = float(1.5707963267948966);
  const angleNorm = fract(HALF_PI.sub(theta).div(TWO_PI));

  // Filled fraction of the ring (1 = full, 0 = empty).
  const filled = float(1.0).sub(saturate(aCooldownFraction));
  // Soft edge so the depleting end fades instead of a hard cut.
  const visibility = smoothstep(filled.add(0.01), filled.sub(0.01), angleNorm);

  // Brighter when fully ready; softer while cooling down.
  const readyBoost = float(1.0).add(filled.mul(0.6));
  const color = vInstanceColor.mul(readyBoost);
  // Base ring opacity — high enough to read against the terrain but subtle.
  const alpha = visibility.mul(0.7);
  return vec4(color, alpha);
});
